import { useEffect, useState } from 'react';
import MetricsCsvService from '@avtechsoftware/ra-javascript-utilities/src/utilities/metricsGroups/MetricsCsvService';
import ChannelService
  from '@avtechsoftware/ra-javascript-utilities/src/utilities/channels/ChannelService';
import AppTypes from '@avtechsoftware/ra-javascript-utilities/src/types/AppTypes';
import ChannelKey from '@avtechsoftware/ra-javascript-utilities/src/classes/ChannelKey';
import ThresholdRangeService
  from '@avtechsoftware/ra-javascript-utilities/src/utilities/metricsGroups/ThresholdRangeService';
import { findReadingTypeById }
  from '@avtechsoftware/ra-javascript-utilities/src/types/ReadingType';
import dashboardApi from '../../services/dashboardApi';
import {
  SENSOR_DATA_DEFAULT_CONTEXT,
  isGraphVisible, SENSOR_DATA_SOURCE_TYPE,
} from './sensorDataWidgetTypes';

const EM_DASH = '—';

// Translate the app-wide userPreferences shape into the options bag that
// channel instance methods (getDisplayValue/getUnits/…) expect. Channels
// read `scale` for temperature; airSpeedScale keeps its name.
const toChannelOptions = (userPreferences = {}) => ({
  scale: userPreferences.temperatureScale,
  airSpeedScale: userPreferences.airSpeedScale,
});

const getChannelInstance = (raw) => {
  if (!raw || !raw.channelKey) return null;
  const key = new ChannelKey(raw.channelKey);
  return ChannelService.getChannelGroupChannelByAppType(
    key.channelGroupType,
    key.channelIndex,
    key.channelType,
    AppTypes.CLOUD,
  );
};

const toDisplayValue = (channel, value, channelOptions) => {
  if (channel && typeof channel.getDisplayValue === 'function') {
    const converted = channel.getDisplayValue(value, channelOptions);
    const num = Number(converted);
    return Number.isFinite(num) ? num : value;
  }
  return value;
};

const convertThresholdValueToDisplay = (value, readingTypeId, temperatureScale) => {
  if (value == null) return value;
  const meta = findReadingTypeById(readingTypeId);
  if (!meta || typeof meta.toUserDisplayValue !== 'function') return value;
  return meta.toUserDisplayValue(value, { temperatureScale });
};

// Human-readable label for the "Sensor Channel" column / graph legend suffix.
const toSeriesLabel = (raw) => {
  const channel = getChannelInstance(raw);
  if (channel && channel.displayName) return channel.displayName;

  const type = (raw && (raw.channelGroupType || raw.channelType)) || '';
  return type
    .toString()
    .toLowerCase()
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (c) => c.toUpperCase());
};

const formatLastReading = (raw, userPreferences = {}) => {
  if (!raw) return EM_DASH;
  const value = raw.lastSampleValue;
  if (value == null || value === '') return EM_DASH;
  const channel = getChannelInstance(raw);
  if (channel && typeof channel.getDisplayValueWithUnits === 'function') {
    return channel.getDisplayValueWithUnits(value, {
      ...toChannelOptions(userPreferences),
      customUnits: raw.customUnit || null,
    });
  }
  return raw.customUnit ? `${value}${raw.customUnit}` : String(value);
};

const toTableRow = (raw, userPreferences = {}) => ({
  channelId: raw.channelId,
  sensorName: raw.sensorName || raw.deviceName || '',
  component: raw.componentName || '',
  sensorChannel: toSeriesLabel(raw),
  lastReading: formatLastReading(raw, userPreferences),
  color: raw.color || null,
});

// Turn CSV rows (parsed by MetricsCsvService) into
// { [channelId]: [{ time, value }, ...] } sorted ascending by time.
const csvRowsToSamplesByChannelId = (rows) => {
  if (!rows || rows.length === 0) return {};

  const channelIds = Object.keys(rows[0]).filter((k) => k !== 'Date');
  const result = Object.fromEntries(channelIds.map((id) => [id, []]));

  rows.forEach((row) => {
    const ts = Number(row.Date);
    if (!Number.isFinite(ts)) return;
    const time = new Date(ts * 1000);

    channelIds.forEach((id) => {
      const raw = row[id];
      if (raw === undefined || raw === null || raw === '') return;
      const value = Number(raw);
      if (!Number.isNaN(value)) result[id].push({ time, value });
    });
  });

  channelIds.forEach((id) => result[id].sort((a, b) => a.time - b.time));
  return result;
};

// Combine channel metadata (label, color) with samples into the series array
// Plotly consumes. Values are routed through the channel instance so
// temperature samples honor the user's preferred scale.
const buildSeries = (channels, samplesByChannelId, userPreferences) => {
  const channelOptions = toChannelOptions(userPreferences);
  return channels.map((ch) => {
    const instance = getChannelInstance(ch);
    const samples = samplesByChannelId[String(ch.channelId)] || [];

    const units = instance && typeof instance.getUnits === 'function'
      ? instance.getUnits(channelOptions)
      : (ch.customUnit || '');

    return {
      channelId: ch.channelId,
      sensorName: ch.sensorName || ch.deviceName || '',
      name: toSeriesLabel(ch),
      color: ch.color || null,
      units,
      x: samples.map((s) => s.time),
      y: samples.map((s) => toDisplayValue(instance, s.value, channelOptions)),
      text: samples.map((s) => {
        if (instance && typeof instance.getDisplayValue === 'function') {
          const display = instance.getDisplayValue(s.value, channelOptions);
          return display == null ? '' : String(display);
        }
        return String(s.value);
      }),
    };
  });
};

const emptyData = () => ({
  series: [], rows: [], samplesLoading: false, samplesError: false,
});

// ---------------------------------------------------------------------------

const normalizeThresholdRange = (assoc, { temperatureScale } = {}) => {
  const range = assoc.thresholdRange || {};
  const readingTypeId = range.readingType;
  return {
    typeId: range.typeId,
    groupId: range.thresholdRangeGroup && range.thresholdRangeGroup.id,
    operatorId: range.operatorId,
    value: convertThresholdValueToDisplay(range.value, readingTypeId, temperatureScale),
    altValue: convertThresholdValueToDisplay(range.altValue, readingTypeId, temperatureScale),
    channelTypeId: range.channelTypeId,
    defaultColor: assoc.color || range.customColor || null,
  };
};

// Fetch channels + samples for a metric-group-backed widget. Channels come
// back synchronously and drive both the table and graph legend. Samples
// stream in second and hydrate the graph (only when the graph is visible).
const fetchMetricGroupData = async (widgetId, { needsSamples, userPreferences }) => {
  if (widgetId == null) return emptyData();

  const resp = await dashboardApi.sensorData.getChannels(widgetId);
  const body = dashboardApi.unwrapBody(resp) || {};
  const channels = body.channels || [];
  const metricGroupName = body.metricGroupName || null;
  const rows = channels.map((ch) => toTableRow(ch, userPreferences));

  const thresholdRanges = (body.thresholdRanges || [])
    .map((assoc) => normalizeThresholdRange(assoc, userPreferences));
  const graphOverlays = ThresholdRangeService
    .getGraphOverlaysFromThresholdRanges(thresholdRanges);

  if (channels.length === 0) {
    return {
      ...emptyData(), rows, metricGroupName, graphOverlays,
    };
  }

  return {
    channels,
    rows,
    metricGroupName,
    graphOverlays,
    series: buildSeries(channels, {}, userPreferences),
    samplesLoading: needsSamples,
    samplesError: false,
    _pendingSamples: needsSamples,
  };
};

// eslint-disable-next-line import/prefer-default-export
export const useSensorDataWidgetData = (
  context,
  {
    isEditMode = false, widgetId = null, userPreferences = {}, refreshTick = 0,
  } = {},
) => {
  const [isLoading, setIsLoading] = useState(true);
  const [data, setData] = useState(null);
  const [isDefault, setIsDefault] = useState(false);

  const {
    sourceType,
    metricGroupId,
    timeRange,
    displayType,
  } = { ...SENSOR_DATA_DEFAULT_CONTEXT, ...context };

  const needsGraph = isGraphVisible(displayType);
  const { temperatureScale, airSpeedScale } = userPreferences;

  useEffect(() => {
    if (isEditMode) return undefined;
    let cancelled = false;
    setIsLoading(true);

    if (widgetId == null
      || (sourceType === SENSOR_DATA_SOURCE_TYPE.METRIC_GROUP && metricGroupId == null)) {
      setData(emptyData());
      setIsDefault(false);
      setIsLoading(false);
      return () => { cancelled = true; };
    }

    fetchMetricGroupData(widgetId, { needsSamples: needsGraph, userPreferences })
      .then((result) => {
        if (cancelled) return;
        const { _pendingSamples, channels, ...rest } = result;

        const nextIsDefault = sourceType === SENSOR_DATA_SOURCE_TYPE.HIGH_LOW
          && (!channels || channels.length === 0);
        setIsDefault(nextIsDefault);

        setData(rest);
        setIsLoading(false);

        if (!_pendingSamples) return;

        const url = dashboardApi.sensorData.buildSamplesUrl(
          widgetId,
        );

        MetricsCsvService.loadCsvData(url, (row) => row)
          .then((rows) => {
            if (cancelled) return;
            const samplesByChannelId = csvRowsToSamplesByChannelId(rows);
            setData((prev) => (prev ? {
              ...prev,
              series: buildSeries(channels, samplesByChannelId, userPreferences),
              samplesLoading: false,
              samplesError: false,
            } : prev));
          })
          .catch(() => {
            if (cancelled) return;
            setData((prev) => (prev ? {
              ...prev,
              samplesLoading: false,
              samplesError: true,
            } : prev));
          });
      })
      .catch(() => {
        if (cancelled) return;
        setData({ ...emptyData(), samplesError: true });
        setIsDefault(true);
        setIsLoading(false);
      });

    return () => { cancelled = true; };
  }, [
    sourceType, metricGroupId, timeRange,
    isEditMode, widgetId, needsGraph, temperatureScale, airSpeedScale, refreshTick,
  ]);

  return { isLoading, data, isDefault };
};
